import { useEffect, useLayoutEffect, useRef } from 'react';
import type { NavigationType } from 'react-router-dom';

// "Scroll position MUST be preserved when navigating back to a list" (design system 02-patterns §4; Phase 1c finding
// D21). Calis runs on `BrowserRouter`, which keeps no positions, and its lists load their rows after the route renders:
// going Back, the list remounted on its skeleton, the browser restored against that short document and clamped — To-dos
// at 1280 went from 1377 pixels to 0, on a phone from 4370 to 264. A data router's `<ScrollRestoration>` would clamp the
// same way, since nothing here loads before it renders. So this remembers where each history entry was scrolled and,
// going Back or Forward to it, takes the reader there once the page can hold the position and its regions have stopped
// loading — following the page down as it grows, and giving the place up to the reader the moment they move.

/** The part of the Web Storage interface used here; null where storage is absent or refused. */
export type SessionStore = Pick<Storage, 'getItem' | 'setItem'>;

export const SCROLL_MEMORY_STORAGE_KEY = 'calis:scroll-positions';
/** At most this many history entries' positions are kept; the least recently scrolled go first. */
export const SCROLL_MEMORY_MAX_ENTRIES = 50;
/**
 * How long a restore waits for the page to hold the position with nothing still loading, before it settles for what
 * the page can hold. A list whose rows never arrive must not hold the reader's scroll hostage.
 */
export const SCROLL_RESTORE_GIVE_UP_MS = 5000;

/** Which history entry a location is: the router's own key for the entry, with its address beside it. */
export function scrollEntryKey(location: { key: string; pathname: string; search: string }): string {
  // Why the address too: the router names the first entry of every page load "default", so two loads' first entries
  // would otherwise share one place.
  return `${location.key}|${location.pathname}${location.search}`;
}

export interface ScrollPositions {
  get: (entry: string) => number | undefined;
  set: (entry: string, y: number) => void;
  /** Writes the positions to the session's store (kept in memory between writes, so a scroll costs no storage write). */
  persist: () => void;
}

/** The positions, read once from `store` and written back to it on `persist`. */
export function createScrollPositions(store: SessionStore | null): ScrollPositions {
  const positions = new Map<string, number>();
  try {
    const stored = store?.getItem(SCROLL_MEMORY_STORAGE_KEY) ?? null;
    const parsed: unknown = stored === null ? null : JSON.parse(stored);
    if (Array.isArray(parsed)) {
      for (const pair of parsed) {
        if (Array.isArray(pair) && typeof pair[0] === 'string' && typeof pair[1] === 'number' && Number.isFinite(pair[1])) positions.set(pair[0], pair[1]);
      }
    }
  } catch {
    // Nothing remembered: every page starts at its top, as before.
  }
  return {
    get: (entry) => positions.get(entry),
    set: (entry, y) => {
      // Why delete first: a Map keeps insertion order, so the entry just scrolled moves to the end and the oldest go first.
      positions.delete(entry);
      positions.set(entry, Math.max(0, Math.round(y)));
      while (positions.size > SCROLL_MEMORY_MAX_ENTRIES) {
        const oldest = positions.keys().next().value;
        if (oldest === undefined) break;
        positions.delete(oldest);
      }
    },
    persist: () => {
      try {
        store?.setItem(SCROLL_MEMORY_STORAGE_KEY, JSON.stringify([...positions]));
      } catch {
        // Not remembered past this page load; Back within it still finds its place.
      }
    },
  };
}

export interface RestoreInput {
  /** The position remembered for the entry. */
  target: number;
  /** How far the document can scroll now. */
  maxScroll: number;
  /** True while a region of the page says it is still loading (`aria-busy`). */
  busy: boolean;
  elapsedMs: number;
}

export interface RestoreStep {
  /** Where to scroll now: the target where the page can hold it, else as near as it can. */
  scrollTo: number;
  /** True once the restore is over: the target held with nothing loading, or the wait given up. */
  done: boolean;
}

/** One frame of a restore (pure). */
export function restoreStep({ target, maxScroll, busy, elapsedMs }: RestoreInput): RestoreStep {
  const reachable = Math.max(0, Math.min(target, maxScroll));
  if (maxScroll >= target && !busy) return { scrollTo: target, done: true };
  return { scrollTo: reachable, done: elapsedMs >= SCROLL_RESTORE_GIVE_UP_MS };
}

/**
 * Whether the page load itself is a return to an entry — a reload, or Back or Forward to a page the browser had to load
 * again — rather than a fresh visit, which starts at the top whatever an earlier visit left behind.
 */
export function isReturnLoad(type: string | undefined): boolean {
  return type === 'reload' || type === 'back_forward';
}

let shared: ScrollPositions | null = null;

function sessionPositions(): ScrollPositions {
  if (shared) return shared;
  let store: SessionStore | null = null;
  try {
    store = typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    store = null;
  }
  shared = createScrollPositions(store);
  return shared;
}

function loadType(): string | undefined {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return entry?.type;
  } catch {
    return undefined;
  }
}

/** Whether a region of the page is still loading. */
function pageBusy(): boolean {
  return document.querySelector('#main [aria-busy="true"]') !== null;
}

// Why these end a restore: each is the reader moving on their own — a scroll by wheel, touch or key, or a press.
const READER_MOVES = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

/**
 * Remembers each history entry's scroll position and restores it on Back and Forward (02-patterns §4); every other
 * change of page goes to the top, and a change of the address on the same page (a sort, a filter, a tab) keeps the place.
 * `entry` is `scrollEntryKey` of the location; `page` is the page it shows (`pageKeyOf`).
 */
export function useScrollMemory(entry: string, page: string, navigationType: NavigationType): void {
  const positions = sessionPositions();
  const current = useRef(entry);
  const restoring = useRef(false);
  const lastPage = useRef<string | null>(null);

  // Why manual: the browser's own restoration runs against the document as it stands when the entry is reached — the
  // list's skeleton — and clamps; this takes the decision over, for the whole session of the app.
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  // Why every scroll event, into memory alone: browsers already send at most one a frame, and a position read a frame
  // late is read after the next page's render has clamped it. Storage is written on leaving an entry and on page hide.
  useEffect(() => {
    const onScroll = (): void => {
      if (!restoring.current) positions.set(current.current, window.scrollY);
    };
    const onHide = (): void => {
      if (!restoring.current) positions.set(current.current, window.scrollY);
      positions.persist();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
    };
  }, [positions]);

  // Why a layout effect: the decision is made after the new entry's page is written and before it paints, so a page
  // that goes to the top never paints a frame at the old one's place, and a restore starts as near its target as it can.
  useLayoutEffect(() => {
    const previousPage = lastPage.current;
    lastPage.current = page;
    current.current = entry;
    positions.persist();

    if (previousPage === null) {
      // The page load: a reload or a return restores; a fresh visit starts at the top and forgets what an earlier load
      // left under the same entry.
      if (!isReturnLoad(loadType())) {
        positions.set(entry, 0);
        return undefined;
      }
    } else if (navigationType !== 'POP') {
      if (previousPage === page) positions.set(entry, window.scrollY);
      else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return undefined;
    }

    const target = positions.get(entry);
    if (target === undefined || target <= 0) {
      if (previousPage !== null && previousPage !== page) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return undefined;
    }

    restoring.current = true;
    const started = performance.now();
    let frame = 0;
    // Why `record`: a restore that ends here — held, given up, or handed to the reader — notes where the entry now is; one
    // cut short by the next navigation does not, since the next page's render has already moved the scroll.
    const finish = (record: boolean): void => {
      cancelAnimationFrame(frame);
      for (const kind of READER_MOVES) window.removeEventListener(kind, onReaderMove, true);
      if (!restoring.current) return;
      restoring.current = false;
      if (record) positions.set(current.current, window.scrollY);
    };
    const onReaderMove = (): void => finish(true);
    const tick = (): void => {
      const step = restoreStep({
        target,
        maxScroll: document.documentElement.scrollHeight - window.innerHeight,
        busy: pageBusy(),
        elapsedMs: performance.now() - started,
      });
      if (Math.abs(window.scrollY - step.scrollTo) >= 1) window.scrollTo({ top: step.scrollTo, left: 0, behavior: 'auto' });
      if (step.done) {
        finish(true);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    for (const kind of READER_MOVES) window.addEventListener(kind, onReaderMove, { capture: true, passive: true });
    tick();
    return () => finish(false);
    // Why the entry alone: it changes with every navigation, and `page` and the navigation's type change only with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);
}
